"use strict";

class DebugPage {
  static async init() {
    $("#download-debug").on("click", () =>
      OfflineExporter.exportAndDownloadOfflineData({
        onlyIncludeObjectStores: ["cachedDefaultStore", "cachedMetadataStore"],
      }),
    );
    $("#download-photos").on("click", () =>
      OfflineExporter.exportAndDownloadOfflineData({
        onlyIncludeObjectStores: ["cachedFileStore"],
      }),
    );

    DebugPage.initializePhotoListeners();
    DebugPage.testAndDisplayResults(DebugPage.runTests());
  }

  static getTests() {
    return [...DebugPage.getBasicNetworkTests(), ...DebugPage.getLoggedInUserTests()];
  }

  static getBasicNetworkTests() {
    const Network = require("./network");
    const Config = require("./config");
    const ResourceController = require("./offline/resourceController");
    const apiUrl = Config.get().apiUrl;
    const g2Url = "https://g2.2nform.com";

    return [
      {
        runTest: () => ResourceController.init(false),
        getPassText: () => "Offline database initialized successfully",
        getFailText: (e) => `Error initializing offline database: ${e.message}`,
      },
      {
        runTest: async () => {
          if (!("serviceWorker" in navigator)) {
            throw new Error("service workers not supported in this browser");
          }

          const registration = await navigator.serviceWorker.getRegistration("/");
          if (!registration) {
            throw new Error("not registered");
          }

          const worker = registration.active || registration.waiting || registration.installing;
          if (!worker) {
            throw new Error("no worker found in registration");
          }

          const cacheNames = await caches.keys();
          const precacheName = cacheNames.find((name) => name.startsWith("workbox-precache"));
          let precacheCount = 0;
          if (precacheName) {
            const precache = await caches.open(precacheName);
            const precacheKeys = await precache.keys();
            precacheCount = precacheKeys.length;
          }

          const indexCache = await caches.open("index.html");
          const indexKeys = await indexCache.keys();

          if (registration.installing) {
            throw new Error(`caching in progress (${precacheCount} assets cached so far)`);
          }

          if (registration.waiting) {
            throw new Error(`update waiting to activate (${precacheCount} assets cached)`);
          }

          return { precacheCount, indexCached: indexKeys.length > 0 };
        },
        getPassText: (result) =>
          `Service worker active, ${result.precacheCount} assets cached, index ${result.indexCached ? "ready" : "not cached"}`,
        getFailText: (e) => `Service worker: ${e.message}`,
      },
      {
        runTest: () => Network.asPromise(Network.api("get", "/test")),
        getPassText: () => `Successfully connected to ${apiUrl}`,
        getFailText: (e) => `Unable to connect to ${apiUrl}`,
      },
      {
        runTest: () => Network.fetchFrom(`${g2Url}/ows?service=WMS&request=GetCapabilities`),
        getPassText: () => `Successfully connected to ${g2Url}`,
        getFailText: (e) => `Unable to connect to ${g2Url}`,
      },
      {
        runTest: () => {
          const canvas = document.createElement("canvas");

          try {
            const gl = canvas.getContext("webgl", { failIfMajorPerformanceCaveat: true });

            if (!gl) {
              throw new Error("hardware acceleration unavailable");
            }
          } finally {
            canvas.remove();
          }
        },
        getPassText: () => "GPU acceleration enabled",
        getFailText: (e) => `GPU acceleration failed: ${e.message}`,
      },
    ];
  }

  static getLoggedInUserTests() {
    return [
      {
        blocking: true,
        runTest: async () => {
          const TokenManager = require("./login/tokenManager");
          const ToolSettings = require("./settings/toolSettings");
          const FormSettings = require("./settings/formSettings");
          const Session = require("./login/session");
          const ApiCalls = require("./apiCalls");

          const token = TokenManager.loadToken();
          if (!token) {
            throw new Error("please login from the homepage to enable all tests");
          }

          Session.saveContext(TokenManager.getTokenData(token).context);
          const data = await ApiCalls.getToolSettings();
          ToolSettings.saveSettings(data);
          FormSettings.saveSettings(data["form"]);
        },
        getPassText: () => "Tool settings loaded",
        getFailText: (e) => `Unable to login, additional network tests skipped: ${e.message}`,
      },
      {
        runTest: async () => {
          const DirectUpload = require("./files/directUpload");
          const testFile = new File([`debug test ${Date.now()}`], "debug-test.txt", {
            type: "text/plain",
          });
          return await DirectUpload.uploadFile(testFile);
        },
        getPassText: () => "S3 file upload working",
        getFailText: (e) => `S3 upload failed: ${e.message}`,
      },
      {
        runTest: async () => {
          const result = await Sentry.diagnoseSdkConnectivity();

          if (result) {
            throw new Error(result);
          }
        },
        getPassText: () => "Sentry connection verified",
        getFailText: (e) => `Sentry test failed: ${e.message}`,
      },
      {
        runTest: async () => {
          const ToolSettings = require("./settings/toolSettings");
          const Network = require("./network");
          const { getBasemapStyleUrl } = require("esri-leaflet-vector/src/Util");

          const apiKey = ToolSettings.getSetting("esri", "authkey");
          const styleUrl = getBasemapStyleUrl("ArcGIS:Imagery", apiKey);
          const styleResponse = await Network.fetchFrom(styleUrl);

          if (!styleResponse.ok) {
            throw new Error(`style fetch failed: HTTP ${styleResponse.status}`);
          }

          const style = await styleResponse.json();

          if (!style.sources) {
            throw new Error("style JSON missing 'sources' property");
          }

          const sources = Object.entries(style.sources);
          if (sources.length === 0) {
            throw new Error("style JSON has no sources defined");
          }

          for (const [name, source] of sources) {
            if (!source.tiles || source.tiles.length === 0) {
              throw new Error(`source '${name}' has no tiles defined`);
            }

            const tileUrl = source.tiles[0]
              .replace("{z}", "0")
              .replace("{y}", "0")
              .replace("{x}", "0");

            const tileResponse = await Network.fetchFrom(tileUrl);
            if (!tileResponse.ok) {
              throw new Error(`tile fetch for '${name}' failed: HTTP ${tileResponse.status}`);
            }
          }
        },
        getPassText: () => "Esri base map accessible",
        getFailText: (e) => `Esri base map test failed: ${e.message}`,
      },
      {
        runTest: () => DebugPage.testIndexedDbFileUpload(),
        getPassText: () => "IndexedDB file uploads with its body intact",
        getFailText: (e) => `IndexedDB file upload dropped the body: ${e.message}`,
      },
    ];
  }

  // iOS 26.5.2 serializes an IndexedDB-backed file to an empty request body
  static async testIndexedDbFileUpload() {
    const Network = require("./network");
    const ApiError = require("./errors/apiError");

    const file = new File([new Uint8Array(3)], "payload.jpg", { type: "image/jpeg" });
    const storedFile = await DebugPage.storeAndReadFileFromIndexedDb(file);

    const formData = new FormData();
    formData.append("file", storedFile, storedFile.name);

    const request = Network.apiPostWithFormData("/debug/upload-check", formData);
    await Network.asPromise(request).catch(ApiError.assertIs);

    if (request.status !== 204) {
      throw new Error(`expected HTTP 204, received ${request.status}`);
    }
  }

  static async storeAndReadFileFromIndexedDb(file) {
    const CachedObjectStore = require("./offline/cachedObjectStore");
    const Database = require("./offline/database");

    const db = new Database("test-upload", [
      {
        key: "files",
        migration: (migrationDb) => migrationDb.createObjectStore("files", { keyPath: "id" }),
      },
    ]);

    const store = new CachedObjectStore(db, "files", "id");
    store.put({ id: "payload", file });
    await store.getDbDonePromise();

    return (await store.get("payload")).file;
  }

  static testAndDisplayResults(tests) {
    const $ul = $("#debug-tests");

    for (const testPromise of tests) {
      const $li = $("<li>Running test...</li>");
      $ul.append($li);

      testPromise
        .then(({ passed, message }) => $li.text(`${passed ? "✔️" : "❌"} ${message}`))
        .catch((error) => $li.text(`❌ ${error?.message ?? "Unknown error"}`));
    }
  }

  static initializePhotoListeners() {
    const $fileInput = $("#photo-compression-input");

    $("#test-photo-compression").on("click", () => {
      $fileInput.trigger("click");
    });

    $fileInput.on("change", (e) => {
      const file = e.currentTarget.files[0];
      if (!file) return;

      DebugPage.testAndDisplayResults([
        DebugPage.runTest({
          runTest: () => DebugPage.runPhotoCompressionTest(file),
          getPassText: (result) => "Photo compressed, saved offline, and retrieved successfully",
          getFailText: (e) => `Photo test failed: ${e.message}`,
        }),
      ]);

      $("#photo-compression-input").val("");
    });
  }

  static async runPhotoCompressionTest(file) {
    const ImageResizer = require("./files/imageResizer");
    const resizer = new ImageResizer(file);
    const compressedFile = await resizer.resize();

    if (compressedFile === file) {
      throw new Error(`Photo compression skipped - please try again with a larger image.`);
    }

    const CachedObjectStore = require("./offline/cachedObjectStore");
    const Database = require("./offline/database");
    const objectStoreName = "photo";
    const keyPath = "id";

    const db = new Database("test", [
      {
        key: "photo",
        migration: function (migrationDb) {
          migrationDb.createObjectStore(objectStoreName, { keyPath });
        },
      },
    ]);
    const cachedMainObjectStore = new CachedObjectStore(db, objectStoreName, keyPath);
    cachedMainObjectStore.put({ id: 1, compressedFile });
    await cachedMainObjectStore.getDbDonePromise();

    const secondCachedMainObjectStore = new CachedObjectStore(db, objectStoreName, keyPath);
    const result = await secondCachedMainObjectStore.get(1);

    if (result?.compressedFile?.size !== compressedFile.size) {
      throw new Error("Unable to save photo offline or retrieve it correctly.");
    }
  }

  static runTests() {
    const results = [];
    const blocked = [];

    for (const test of DebugPage.getTests()) {
      const resultPromise = Promise.all(blocked).then((blockedResults) => {
        if (blockedResults.some((r) => !r.passed)) {
          return { passed: false, message: "Skipped" };
        }
        return DebugPage.runTest(test);
      });

      results.push(resultPromise);

      if (test.blocking) {
        blocked.push(resultPromise);
      }
    }

    return results;
  }

  static async runTest({ runTest, getPassText, getFailText }) {
    try {
      const result = await runTest();
      return {
        passed: true,
        message: getPassText(result),
      };
    } catch (e) {
      return {
        passed: false,
        message: getFailText(e),
        error: {
          message: e?.message,
          stack: e?.stack,
        },
      };
    }
  }
}

module.exports = DebugPage;

const OfflineExporter = require("./offline/offlineExporter");
const Sentry = require("@sentry/browser");
